import React, { useState } from 'react';
import { X, Loader2, ImagePlus, Star, Trash, AlertCircle, Check } from 'lucide-react';
import VariantImageManager from './VariantImageManager';
import heic2any from 'heic2any';

export interface ProductImage {
  id: number;
  url: string;
  principal: boolean;
  orden: number;
  producto_variante_id: number;
  variante_id?: string;
  tempFile?: File; // For temporary files before upload
  fileName?: string; // Original filename for saving
}

export interface Variant {
  id: string;
  nombre: string;
  image_url?: string;
  images?: ProductImage[];
  created_at?: string; // Database timestamp for ordering variants
}

interface VariantFormProps {
  editingVariant: Variant | null;
  newProduct: { nombre: string; images: ProductImage[] };
  loading: boolean;
  uploadingImages: Record<string, boolean>;
  imageError: string | null;
  onCancel: () => void;
  onSave: () => void; // This will be our unified save function for both adding and updating
  onImageUpload: (variantId: string, file: File) => void;
  onDeleteImage: (variantId: string, imageId: number) => void;
  onSetPrincipalImage: (variantId: string, imageId: number) => void;
  onReorderImages: (variantId: string, imageId: number, direction: 'up' | 'down') => void;
  setEditingVariant: React.Dispatch<React.SetStateAction<Variant | null>>;
  setNewProduct: React.Dispatch<React.SetStateAction<{ nombre: string; images: ProductImage[] }>>;
  onDeleteVariant?: (variantId: string) => void;
  rubro?: string;
}

// Temporary Image Manager component for handling images before uploading
export const TempImageManager = ({
  images,
  imageError,
  onChange,
  rubro
}: {
  images: ProductImage[],
  imageError: string | null,
  onChange: (images: ProductImage[]) => void,
  rubro?: string
}) => {
  // Determine which images and instructions to show based on rubro
  const isFashion = rubro === 'Moda e Indumentaria';
  const isHomeDecor = rubro === 'Hogar y Decoración';
  
  const getExampleImages = () => {
    if (isFashion) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto siendo utilizado'
      };
    } else if (isHomeDecor) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    } else {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    }
  };
  
  const exampleImages = getExampleImages();
  const [showLastImageWarning, setShowLastImageWarning] = useState(false);
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Validate file size (limit: 100 MB)
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      if (file.size > MAX_FILE_SIZE) {
        alert('El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.');
        e.target.value = '';
        return;
      }
      const newImageId = -1 * (images.length + 1); // Use negative IDs for temp images
      const isPrincipal = false; // Never set new images as principal by default
      
      try {
        // Check if the file is a HEIC image
        let processedFile = file;
        let processedUrl = '';
        
        if (
          file.type === 'image/heic' || 
          file.type === 'image/heif' || 
          file.name.toLowerCase().endsWith('.heic') || 
          file.name.toLowerCase().endsWith('.heif')
        ) {
          console.log('Converting HEIC image to JPEG...');
          try {
            // Convert HEIC to JPEG using heic2any
            const convertedBlob = await heic2any({
              blob: file,
              toType: 'image/jpeg',
              quality: 0.8
            });
            
            // Create a new File object from the converted blob
            const convertedFile = new File(
              [convertedBlob as Blob], 
              file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), 
              { type: 'image/jpeg' }
            );
            
            processedFile = convertedFile;
            processedUrl = URL.createObjectURL(convertedBlob as Blob);
            console.log('HEIC image successfully converted to JPEG');
          } catch (err) {
            throw new Error('No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg');
          }
        } else {
          // For non-HEIC images, just use the original file
          processedUrl = URL.createObjectURL(file);
        }
        
        // Create new temp image entry
        const newImage: ProductImage = {
          id: newImageId,
          url: processedUrl,
          principal: isPrincipal,
          orden: images.length + 1,
          producto_variante_id: 0,
          tempFile: processedFile
        };
        
        onChange([...images, newImage]);
      } catch (error) {
        console.error('Error processing image file:', error);
        alert(error instanceof Error ? error.message : 'Error al procesar la imagen. Por favor, intente con otra imagen.');
      }
      
      e.target.value = ''; // Reset input
    }
  };
  
  const handleDelete = (imageId: number) => {
    // Check if this is the last image
    if (images.length <= 1) {
      setShowLastImageWarning(true);
      return;
    }
    
    // Filter out the image to delete
    const updatedImages = images.filter(img => img.id !== imageId);
    
    // If we deleted the principal image and there are no principal images left, set the first one as principal
    if (updatedImages.length > 0 && !updatedImages.some(img => img.principal)) {
      updatedImages[0].principal = true;
    }
    
    onChange(updatedImages);
    
    // Revoke object URL to avoid memory leaks
    const deletedImg = images.find(img => img.id === imageId);
    if (deletedImg && deletedImg.id < 0) { // Only for temp images
      URL.revokeObjectURL(deletedImg.url);
    }
  };
  
  const handleSetPrincipal = (imageId: number) => {
    const updatedImages = images.map(img => ({
      ...img,
      principal: img.id === imageId
    }));
    
    onChange(updatedImages);
  };
  
  return (
    <div className="mt-4">
      <div className="mb-3">
        <input
          type="file"
          id="temp-image-upload"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
        {/* Guidance panel: correct vs incorrect examples */}
        {images.length === 0 && (
          <div className="mt-3 bg-white border border-gray-200 rounded-2xl p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              {/* Thumbnails row */}
              <div className="w-full sm:w-auto flex items-center gap-3">
                <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                  <div className="w-full aspect-square sm:w-28 sm:h-28 sm:aspect-auto rounded-lg border border-gray-200 bg-white overflow-hidden relative">
                    <img
                      src={exampleImages.img1}
                      alt={exampleImages.alt1}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                    <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white shadow-sm">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                  <div className="w-full aspect-square sm:w-28 sm:h-28 sm:aspect-auto rounded-lg border border-gray-200 bg-white overflow-hidden relative">
                    <img
                      src={exampleImages.img2}
                      alt={exampleImages.alt2}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                    <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white shadow-sm">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </div>
              {/* Instructions (inline with thumbnails on desktop, below on mobile) */}
              <ul className="text-xs text-gray-600 list-disc pl-4 space-y-1 sm:mt-0 mt-2">
                <li>Subí una foto en donde se vea claramente el producto que querés vender.</li>
                {isFashion && <li>Fotos del producto siendo utilizado (sin mostrar el rostro) funcionan mejor.</li>}
              </ul>
            </div>
          </div>
        )}

        {/* Upload button below instructions; full width on mobile */}
        <div className="mt-3">
          <label
            htmlFor="temp-image-upload"
            className={`inline-flex w-full sm:w-auto justify-center items-center px-3 py-3 sm:py-2 shadow-sm text-sm font-normal rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ${images.length === 0 ? 'bg-blue-500 text-white hover:bg-blue-600' : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
          >
            <ImagePlus size={16} className="mr-2" />
            {images.length === 0 ? 'Subir imagen del producto' : 'Agregar otra imagen'}
          </label>
        </div>
      </div>
      
      {imageError && (
        <div className="mb-3 text-sm text-red-600">{imageError}</div>
      )}
      
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((image) => (
            <div key={image.id} className="relative border border-gray-200 rounded-2xl overflow-hidden">
              <div className="relative">
                <img 
                  src={image.url} 
                  alt="Imagen de producto"
                  className="w-full h-32 object-cover"
                />
                {image.principal && (
                  <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-md">
                    Principal
                  </div>
                )}
              </div>
              <div className="p-2 bg-white flex justify-between">
                <div className="flex space-x-2">
                  {!image.principal && (
                    <button
                      type="button"
                      onClick={() => handleSetPrincipal(image.id)}
                      title="Establecer como principal"
                      className="text-blue-500 hover:text-blue-600"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(image.id)}
                    title="Eliminar imagen"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Last Image Warning */}
      {showLastImageWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-md shadow-sm max-w-md w-full p-6">
            <div className="flex items-center mb-4 text-amber-600">
              <AlertCircle className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-medium">No se puede eliminar</h3>
              <button 
                onClick={() => setShowLastImageWarning(false)} 
                className="ml-auto text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="mb-4">El producto debe tener al menos una imagen. No se puede eliminar la última imagen.</p>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowLastImageWarning(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VariantForm: React.FC<VariantFormProps> = ({
  editingVariant,
  newProduct,
  loading,
  uploadingImages,
  imageError,
  onCancel,
  onSave,
  onImageUpload,
  onDeleteImage,
  onSetPrincipalImage,
  onReorderImages,
  setEditingVariant,
  setNewProduct,
  onDeleteVariant,
  rubro
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleCancel = () => {
    // If we're adding a new product (not editing), clear temporary state
    if (!editingVariant) {
      // Revoke any object URLs created for temp images to avoid memory leaks
      try {
        newProduct.images.forEach((img) => {
          if (img.id < 0 && img.url && img.url.startsWith('blob:')) {
            URL.revokeObjectURL(img.url);
          }
        });
      } catch (e) {
        // No-op: revocation failures shouldn't block cancel
      }

      // Reset name and images
      setNewProduct({ nombre: '', images: [] });
    }

    // Delegate to parent cancel handler (typically closes the modal)
    onCancel();
  };
  
  // Determine if save is disabled (used for styling)
  const isSaveDisabled = editingVariant ? !editingVariant.nombre : !newProduct.nombre || loading;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-sm max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingVariant ? 'Editar producto' : 'Agregar producto'}
          </h3>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre
            </label>
            <input
              type="text"
              value={editingVariant ? editingVariant.nombre : newProduct.nombre}
              onChange={(e) => {
                if (editingVariant) {
                  setEditingVariant({ ...editingVariant, nombre: e.target.value });
                } else {
                  setNewProduct({ ...newProduct, nombre: e.target.value });
                }
              }}
              className="w-full h-10 px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              placeholder="Nombre del producto"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Imágenes
            </label>
            {editingVariant ? (
              <VariantImageManager
                variantId={editingVariant.id || ''}
                images={editingVariant.images || []}
                uploadingImages={uploadingImages}
                imageError={imageError}
                onUpload={onImageUpload}
                onDelete={onDeleteImage}
                onSetPrincipal={onSetPrincipalImage}
                onReorder={onReorderImages}
                showHeader={false}
              />
            ) : (
              <TempImageManager
                images={newProduct.images}
                imageError={imageError}
                onChange={(newImages) => setNewProduct({...newProduct, images: newImages})}
                rubro={rubro}
              />
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          {/* Delete button on the left, only when editing */}
          {editingVariant && onDeleteVariant ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading}
              className={`h-10 px-4 text-sm rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors bg-red-600 hover:bg-red-700 text-white ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              Eliminar producto
            </button>
          ) : <span />}

          {/* Cancel / Save on the right */}
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="h-10 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              onClick={onSave}
              disabled={isSaveDisabled}
              className={`h-10 px-4 text-sm rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2 ${isSaveDisabled ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                editingVariant ? 'Listo' : 'Guardar producto'
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Delete confirmation modal */}
      {editingVariant && onDeleteVariant && showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-sm max-w-sm w-full p-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 text-red-600">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center">
                  <h3 className="text-base font-semibold text-gray-900">Eliminar producto</h3>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="ml-auto text-gray-400 hover:text-gray-500"
                    aria-label="Cerrar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600">¿Seguro que querés eliminar este producto? Esta acción no se puede deshacer.</p>
                <div className="mt-5 flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="h-9 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => { onDeleteVariant(editingVariant.id); setShowDeleteConfirm(false); }}
                    disabled={loading}
                    className={`h-9 px-4 text-sm rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-red-600 hover:bg-red-700 text-white ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantForm;
